import {
  Box,
  HStack,
  Text,
  Heading,
  Spacer,
  Flex,
  useColorModeValue,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { DataPipeline } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useSlugToName } from 'packages/dataPipeline/hooks/slugToNameContext';
import { getStatusIcon } from 'packages/dataPipeline/ui/list/PipelineStatusIndicator';
import {
  getReportingDateVersionString,
  getReportingDateVersionShortString,
} from 'packages/dataPipeline/ui/utils/getReportingDateVersionString';
import { getRelativeTime } from 'packages/dataPipeline/ui/utils/getTimeElapsed';
import { SelectedMeshRun } from '~/packages/dataPipeline/types/dataPipelineTypes';

export const MeshCard = ({
  meshRow,
}: {
  meshRow: DataPipeline.MeshRunRecord;
}) => {
  const location = useLocation();
  const { cfSlugToName } = useSlugToName();
  const searchParams = location.search;

  const reportingDateVersionStr = getReportingDateVersionString(
    meshRow.reportingDate,
    meshRow.fileVersion,
  );

  const startedAtStr = new Date(meshRow.startedAt).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

  const subTextColor = useColorModeValue(colors.black[70], colors.gray[300]);
  const statusIcon = getStatusIcon(meshRow.status);
  const bgColor = useColorModeValue(colors.background[50], colors.gray[800]);
  const borderColor = useColorModeValue(colors.gray[200], colors.gray[600]);

  return (
    <Box borderWidth="1px" borderRadius="lg" p={0}>
      <Box
        bg={bgColor}
        p="1rem"
        w="100%"
        borderBottom="1px"
        borderColor={borderColor}
        borderTopRadius="lg"
      >
        <Heading size="1rem" fontWeight={500}>
          {meshRow.cfSlugs.map(cfSlugToName).join(', ')}
        </Heading>
      </Box>
      <Box p="1rem">
        <HStack spacing="1.5rem" alignItems="flex-start">
          <Box w="6.25rem" h="3.75rem" textAlign="center">
            <Tooltip label={statusIcon.label} placement="top">
              <Box display="inline-block">
                <FaIcon
                  icon={statusIcon.icon}
                  color={statusIcon.textColor}
                  size="1.5rem"
                />
              </Box>
            </Tooltip>
            <Tooltip label="Last Updated" placement="bottom">
              <Text
                mt="0.5rem"
                fontSize="0.875rem"
                fontWeight={400}
                color={subTextColor}
              >
                {getRelativeTime(meshRow.updatedAt)}
              </Text>
            </Tooltip>
          </Box>
          <Box>
            <Link
              to={`./details/${meshRow.meshRunId}${searchParams}`}
              style={{
                color: colors.blue[400],
                fontSize: '1rem',
                textDecoration: 'underline',
                wordBreak: 'break-all',
              }}
            >
              {meshRow.meshRunId}
            </Link>
            <Flex mt="0.5rem" width="100%" alignItems="center">
              <FaIcon icon="calendar" color={subTextColor} />
              <Text fontSize="0.875rem" color={subTextColor} ml="0.5rem">
                {reportingDateVersionStr}
              </Text>
            </Flex>
          </Box>
          <Spacer />
          <Box>
            <Text
              fontSize="0.875rem"
              fontWeight={400}
              color={subTextColor}
              mt="0"
            >
              Started {startedAtStr}
            </Text>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

export const MeshMiniCard = ({
  meshRow,
  onPipelineSelected,
}: {
  meshRow: DataPipeline.MeshRunRecordWithMeshDocument;
  onPipelineSelected(selectedMeshRun: SelectedMeshRun): void;
}) => {
  const { cfSlugToName } = useSlugToName();
  const reportingDateVersionStr = getReportingDateVersionShortString(
    meshRow.reportingDate,
    meshRow.fileVersion,
  );

  const startedAtStr = new Date(meshRow.startedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

  const statusIcon = getStatusIcon(meshRow.status);

  const meshName =
    meshRow.latestMeshDocument?.meshName ||
    meshRow.cfSlugs.map(cfSlugToName).join(', ');

  return (
    <Box p="0.875rem" borderWidth="1px" borderRadius="lg" width="100%">
      <HStack
        spacing="1.5rem"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <VStack alignItems="flex-start">
          <Typography fontWeight="medium" trim>
            {meshName}
          </Typography>

          <HStack spacing="1rem">
            <Typography variant="detail-muted">
              {reportingDateVersionStr}
            </Typography>
            <Typography variant="detail-muted">
              Started: {startedAtStr}
            </Typography>
          </HStack>
        </VStack>
        <HStack>
          <FaIcon
            icon={statusIcon.icon}
            color={statusIcon.textColor}
            size="1.5rem"
          />
          <Text>{statusIcon.label}</Text>
          <IconButton
            ariaLabel="Open"
            label="Open"
            icon={<FaIcon icon="angle-right" size="1rem" />}
            isDisabled={!meshRow.latestMeshDocument}
            onClick={() => {
              // we allow click only if mesh was run against latest document
              // to prevent selecting stale mesh runs
              if (meshRow.latestMeshDocument) {
                onPipelineSelected({
                  meshRunId: meshRow.meshRunId,
                  meshRunParams: {
                    meshDocumentMetadata: meshRow.latestMeshDocument,
                    rdfv: {
                      reportingDate: meshRow.reportingDate,
                      fileVersion: meshRow.fileVersion,
                    },
                  },
                });
              }
            }}
          />
        </HStack>
      </HStack>
    </Box>
  );
};
